// Worker preferences for a client (Brevity "Allocated Support Workers"): a Preferred (favourite)
// list and an Excluded (not-to-use / banned) list. Preferred workers rank first in the
// Scheduling Assistant; excluded workers are auto-excluded from suggestions.
import React, { useState, useEffect, useCallback } from 'react';
import { Star, Ban, Plus, X, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function ClientWorkerPreferences({ clientId }) {
  const [prefs, setPrefs] = useState({ preferred: [], excluded: [] });
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // 'preferred' | 'excluded' | null

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/clients/${clientId}/worker-preferences`); setPrefs(data); }
    catch { /* tables may be new */ } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => {
    load();
    api.get('/staff').then(r => setStaff((Array.isArray(r.data) ? r.data : r.data.staff || []).filter(s => ['carer', 'support_worker'].includes(s.role)))).catch(() => {});
  }, [load]);

  const add = async (staffId, kind) => {
    try { await api.post(`/clients/${clientId}/worker-preferences`, { staffId, kind }); setAdding(null); load(); }
    catch { toast.error('Failed'); }
  };
  const remove = async (staffId, kind) => {
    try { await api.delete(`/clients/${clientId}/worker-preferences/${staffId}?kind=${kind}`); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return null;
  const usedIds = new Set([...prefs.preferred, ...prefs.excluded].map(p => p.id));
  const available = staff.filter(s => !usedIds.has(s.id));

  const List = ({ kind, title, icon: Icon, accent, items }) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Icon size={13} className={accent} /> {title}</h4>
        <button type="button" onClick={() => setAdding(adding === kind ? null : kind)} aria-expanded={adding === kind} className="inline-flex items-center gap-1.5 h-9 px-2.5 -my-1.5 rounded-lg text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100"><Plus size={14} aria-hidden /> Add</button>
      </div>
      {adding === kind && (
        <select autoFocus aria-label={`Add a worker to ${title.toLowerCase()}`} className="input-field text-sm mb-2" defaultValue="" onChange={e => e.target.value && add(e.target.value, kind)}>
          <option value="">Pick a worker…</option>
          {available.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
        </select>
      )}
      <div className="flex flex-wrap gap-1.5">
        {items.length === 0 ? <span className="text-xs text-slate-500">None yet.</span> : items.map(p => (
          <span key={p.id} className={`inline-flex items-center gap-1 text-xs pl-2.5 pr-1 py-0.5 rounded-full border ${kind === 'preferred' ? 'bg-white border-slate-200 text-slate-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
            {p.name}
            <button type="button" onClick={() => remove(p.id, kind)} aria-label={`Remove ${p.name} from ${title.toLowerCase()}`} className="w-6 h-6 inline-flex items-center justify-center rounded-full hover:bg-slate-100"><X size={12} aria-hidden /></button>
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-slate-100">
        <Star size={15} className="text-slate-400" aria-hidden />
        <h3 className="text-sm font-semibold text-slate-700">Worker preferences</h3>
        <span className="ml-auto text-[12px] text-slate-500">Used by the Scheduling Assistant</span>
      </div>
      <div className="p-5 space-y-4">
        <List kind="preferred" title="Preferred (favourites)" icon={Star} accent="text-amber-500" items={prefs.preferred} />
        <div className="border-t border-slate-50" />
        <List kind="excluded" title="Excluded (not to use)" icon={Ban} accent="text-rose-500" items={prefs.excluded} />
        <p className="text-[12px] text-slate-500">Favourites rank first when staffing this client. Excluded workers are never suggested.</p>
      </div>
    </div>
  );
}
